import { useState, useEffect, useCallback } from "react";

/**
 * Custom hook for managing video playback state and controls
 * @param {React.RefObject} videoRef - Reference to the video element
 * @param {Object} activeHighlight - Currently active highlight (for looping)
 * @param {boolean} isTimelineDragging - Whether timeline is being dragged
 * @returns {Object} Playback state and control functions
 */
const useVideoPlayback = (videoRef, activeHighlight, isTimelineDragging) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleTimeUpdate = () => {
      if (!isTimelineDragging) {
        setCurrentTime(videoElement.currentTime);
      }

      if (activeHighlight) {
        const endTime = activeHighlight.startTime + activeHighlight.duration;
        if (videoElement.currentTime >= endTime) {
          videoElement.currentTime = activeHighlight.startTime;
        }
        videoElement.playbackRate = activeHighlight.slowMotion ? 0.5 : playbackRate;
      } else {
        videoElement.playbackRate = playbackRate;
      }
    };

    const handleVideoEnded = () => {
      if (activeHighlight) {
        videoElement.currentTime = activeHighlight.startTime;
        videoElement.play();
      } else {
        setIsPlaying(false);
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleLoadedMetadata = () => setDuration(videoElement.duration);

    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('ended', handleVideoEnded);
    videoElement.addEventListener('play', handlePlay);
    videoElement.addEventListener('pause', handlePause);
    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('ended', handleVideoEnded);
      videoElement.removeEventListener('play', handlePlay);
      videoElement.removeEventListener('pause', handlePause);
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [videoRef, activeHighlight, playbackRate, isTimelineDragging]);

  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;

    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [videoRef]);

  const cyclePlaybackRate = useCallback(() => {
    setPlaybackRate(current => current >= 4 ? 1 : current + 1);
  }, []);

  const seekTo = useCallback((time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, [videoRef]);

  const formatTime = useCallback((timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  return {
    isPlaying,
    setIsPlaying,
    currentTime,
    setCurrentTime,
    duration,
    playbackRate,
    setPlaybackRate,
    togglePlay,
    cyclePlaybackRate,
    seekTo,
    formatTime,
  };
};

export default useVideoPlayback;
